import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";
import { FaCopy, FaArrowLeft, FaArrowRight, FaThLarge } from "react-icons/fa";
import BasicButton from "../../Buttons/BasicButton";
import { GetCustomDomainDetail, VerifyCustomDomain } from "../../../Api/apis/customDomainApi";
import { CustomDomainDetailData, CustomDomainErrorResponse, DNSRecord } from "../types";

interface CustomDomainVerificationProps {
  domainId: number;
  hostname: string;
  onVerified: () => void;
  onExit: () => void;
}

// The API doesn't return a TTL for these records
const DISPLAY_TTL = "(Automatic)";

const copyToClipboard = async (value: string, successMessage: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(successMessage);
  } catch {
    toast.error("Failed to copy to clipboard");
  }
};

const CustomDomainVerification: React.FC<CustomDomainVerificationProps> = ({
  domainId,
  hostname,
  onVerified,
  onExit,
}) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<CustomDomainDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const resp = await GetCustomDomainDetail(domainId);
      setDetail(resp.data.data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  // Fetch DNS instructions when this step mounts - the domain (and its
  // verification token) only exists once step 1 has already created it.
  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const resp = await VerifyCustomDomain(domainId);
      toast.success(resp.data.message || t("customDomains.verifySuccessFallback"));
      onVerified();
    } catch (error) {
      if (isAxiosError<CustomDomainErrorResponse>(error)) {
        toast.error(error.response?.data?.message || t("customDomains.verifyFailedFallback"));
      } else {
        toast.error(t("customDomains.verifyFailedFallback"));
      }
    } finally {
      setVerifying(false);
    }
  };

  const records: DNSRecord[] = detail
    ? [detail.dns_instructions.verification, detail.dns_instructions.routing]
    : [];

  return (
    <div className="d-flex flex-column gap-3">
      <h3 className="fs-4 fw-bold text-dark mb-0">
        {t("customDomains.verifyOwnershipOf")} <span className="text-primary">{hostname}</span>
      </h3>


      <p className="text-muted mb-0">{t("customDomains.dnsDelegationDescription")}</p>

      <div className="border rounded-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 p-3 border-bottom">
          <div className="d-flex align-items-center gap-2">
            <FaThLarge className="text-muted" />
            <span className="fw-semibold text-uppercase small">
              {t("customDomains.requiredDnsSignatures")}
            </span>
          </div>
          {!loading && !loadError && (
            <span className="text-muted small">
              {t("customDomains.recordsPending", { count: records.length })}
            </span>
          )}
        </div>

        {loading && (
          <div className="p-4 text-center text-muted small">
            <span className="spinner-border spinner-border-sm me-2" />
            {t("customDomains.loadingDnsRecords")}
          </div>
        )}

        {!loading && loadError && (
          <div className="p-4 text-center d-flex flex-column align-items-center gap-2">
            <span className="text-danger small">{t("customDomains.failedToLoadDnsRecords")}</span>
            <BasicButton variant="ghost" size="sm" onClick={fetchDetail}>
              {t("customDomains.tryAgain")}
            </BasicButton>
          </div>
        )}

        {!loading && !loadError && (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-muted small text-uppercase ps-4">{t("customDomains.type")}</th>
                  <th className="text-muted small text-uppercase">{t("customDomains.hostName")}</th>
                  <th className="text-muted small text-uppercase">{t("customDomains.valueTarget")}</th>
                  <th className="text-muted small text-uppercase">{t("customDomains.ttl")}</th>
                  <th className="text-muted small text-uppercase pe-4">{t("customDomains.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={`${record.type}-${record.name}`}>
                    <td className="ps-4">
                      <span className="badge bg-light text-dark border">{record.type}</span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold">{record.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 text-muted"
                          onClick={() => copyToClipboard(record.name, t("customDomains.copied"))}
                        >
                          <FaCopy size={14} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-break">{record.value}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 text-muted"
                          onClick={() => copyToClipboard(record.value, t("customDomains.copied"))}
                        >
                          <FaCopy size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="text-muted">{DISPLAY_TTL}</td>
                    <td className="pe-4">
                      <BasicButton
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          copyToClipboard(
                            `${record.type} ${record.name} ${record.value}`,
                            t("customDomains.copied"),
                          )
                        }
                      >
                        <FaCopy className="me-2" size={12} />
                        {t("customDomains.copyRecord")}
                      </BasicButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      <div className="d-flex justify-content-between flex-wrap gap-2 pt-2">
        <BasicButton variant="ghost" onClick={onExit}>
          <FaArrowLeft className="me-2" />
          {t("customDomains.back")}
        </BasicButton>
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-link text-decoration-none"
            onClick={onExit}
          >
            {t("customDomains.verifyLater")}
          </button>
          <BasicButton onClick={handleVerify} disabled={loading || loadError || verifying}>
            {t("customDomains.verifyNow")}
            <FaArrowRight className="ms-2" />
          </BasicButton>
        </div>
      </div>
    </div>
  );
};

export default CustomDomainVerification;
